import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import VeiculoFormFields from '../components/VeiculoFormFields';
import EnderecoFields from '../components/EnderecoFields';
import { ENDERECO_VAZIO } from '../utils/cep';
import { useModalAberto } from '../contexts/ModalContext';
import toast from 'react-hot-toast';

const veiculoVazio = () => ({ placa: '', marca: '', modelo: '', ano: '', cor: '', km: 0, combustivel: 'flex' });

export default function Clientes() {
  const [busca, setBusca] = useState('');
  const [tipoBusca, setTipoBusca] = useState('cliente');
  const [resultados, setResultados] = useState([]);
  const [buscado, setBuscado] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mostrarTodos, setMostrarTodos] = useState(false);
  const [todos, setTodos] = useState([]);

  const [modal, setModal] = useState(false);
  useModalAberto(modal);
  const [form, setForm] = useState({ nome: '', telefone: '', cpf: '', email: '', ...ENDERECO_VAZIO });
  const [veiculos, setVeiculos] = useState([veiculoVazio()]);
  const [editando, setEditando] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const navigate = useNavigate();

  const carregarTodos = useCallback(async (tipo) => {
    const t = tipo || tipoBusca;
    setLoading(true);
    try {
      if (t === 'cliente') {
        const { data } = await api.get('/clientes');
        setTodos(data);
      } else {
        const { data } = await api.get('/veiculos/buscar');
        setTodos(Array.isArray(data) ? data : []);
      }
      setMostrarTodos(true);
      setBuscado(false);
    } catch { toast.error('Erro ao carregar'); }
    finally { setLoading(false); }
  }, [tipoBusca]);

  useEffect(() => { carregarTodos(tipoBusca); }, [tipoBusca, carregarTodos]);

  const pesquisar = async (e) => {
    e?.preventDefault();
    if (!busca.trim()) return;
    setLoading(true);
    setBuscado(true);
    setMostrarTodos(false);
    try {
      if (tipoBusca === 'cliente') {
        const { data } = await api.get('/clientes', { params: { busca } });
        setResultados(data.map(c => ({ tipo: 'cliente', cliente: c })));
      } else {
        // Busca por veículo — retorna veículos com cliente populado
        const { data: veics } = await api.get('/veiculos/buscar', { params: { busca } });
        const lista = Array.isArray(veics) ? veics : [veics].filter(Boolean);
        setResultados(lista.map(v => ({ tipo: 'veiculo', veiculo: v, cliente: v.cliente })));
      }
    } catch { toast.error('Erro ao buscar'); }
    finally { setLoading(false); }
  };

  const limpar = () => { setBusca(''); setResultados([]); setBuscado(false); carregarTodos(); };

  const abrirNovo = () => {
    setEditando(null);
    setForm({ nome: '', telefone: '', cpf: '', email: '', ...ENDERECO_VAZIO });
    setVeiculos([veiculoVazio()]);
    setModal(true);
  };

  const abrirEditar = (c, e) => {
    e.stopPropagation();
    setEditando(c._id);
    setForm({
      nome: c.nome, telefone: c.telefone, cpf: c.cpf || '', email: c.email || '',
      cep: c.cep || '', endereco: c.endereco || '', numero: c.numero || '', bairro: c.bairro || '', cidade: c.cidade || '', estado: c.estado || '',
    });
    setVeiculos([]);
    setModal(true);
  };

  const adicionarVeiculo = () => setVeiculos(v => [...v, veiculoVazio()]);
  const removerVeiculo = i => setVeiculos(v => v.filter((_, idx) => idx !== i));
  const atualizarVeiculo = (i, novo) => setVeiculos(v => v.map((item, idx) => idx === i ? novo : item));

  const salvar = async e => {
    e.preventDefault();
    setSalvando(true);
    try {
      if (editando) {
        await api.put(`/clientes/${editando}`, form);
        toast.success('Cliente atualizado');
      } else {
        const { data: cliente } = await api.post('/clientes', form);
        const validos = veiculos.filter(v => v.marca && v.modelo);
        await Promise.all(validos.map(v => api.post(`/clientes/${cliente._id}/veiculos`, v)));
        toast.success(`Cliente cadastrado${validos.length ? ` com ${validos.length} veículo(s)` : ''}`);
      }
      setModal(false);
      if (buscado) pesquisar();
      else carregarTodos();
    } catch (err) { toast.error(err.response?.data?.erro || 'Erro ao salvar'); }
    finally { setSalvando(false); }
  };

  const deletar = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Remover este cliente?')) return;
    await api.delete(`/clientes/${id}`);
    toast.success('Cliente removido');
    pesquisar();
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Clientes</h1>
        <button onClick={abrirNovo} className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors">
          + Novo Cliente
        </button>
      </div>

      {/* Área de busca */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
        {/* Tabs de tipo de busca */}
        <div className="flex gap-1 mb-3 bg-gray-100 rounded-lg p-1">
          <button
            type="button"
            onClick={() => { setTipoBusca('cliente'); limpar(); }}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${tipoBusca === 'cliente' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            👤 Por Cliente
          </button>
          <button
            type="button"
            onClick={() => { setTipoBusca('veiculo'); limpar(); }}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${tipoBusca === 'veiculo' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            🚗 Por Veículo
          </button>
        </div>

        {/* Campo de busca */}
        <form onSubmit={pesquisar} className="flex gap-2">
          <input
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder={tipoBusca === 'cliente'
              ? 'Buscar por nome, telefone ou CPF...'
              : 'Buscar por placa, marca ou modelo...'}
            value={busca}
            onChange={e => setBusca(e.target.value)}
          />
          <button type="submit" className="bg-blue-600 text-white px-4 py-2.5 rounded-lg font-medium hover:bg-blue-700 transition-colors">
            Buscar
          </button>
          {buscado && (
            <button type="button" onClick={limpar} className="border border-gray-300 text-gray-600 px-3 py-2.5 rounded-lg hover:bg-gray-50">
              ✕
            </button>
          )}
        </form>
      </div>

      {/* Estado inicial */}
      {!buscado && !mostrarTodos && (
        <div className="text-center py-12 text-gray-400">
          <div className="text-5xl mb-3">🔍</div>
          <p className="text-base font-medium text-gray-500">Pesquise para encontrar clientes</p>
          <p className="text-sm mt-1">Busque por nome, telefone, CPF, placa ou modelo do veículo</p>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-10 text-gray-400">Buscando...</div>
      )}

      {/* Lista completa */}
      {mostrarTodos && !loading && (
        <div>
          <p className="text-sm text-gray-500 mb-2 px-1">
            {todos.length} {tipoBusca === 'cliente' ? 'cliente(s)' : 'veículo(s)'} cadastrado(s)
          </p>
          <div className="bg-white rounded-xl shadow-sm divide-y divide-gray-50">
            {todos.length === 0 && (
              <p className="text-gray-400 text-center py-10">
                Nenhum {tipoBusca === 'cliente' ? 'cliente' : 'veículo'} cadastrado
              </p>
            )}
            {tipoBusca === 'cliente' && todos.map(c => (
              <div key={c._id} className="p-4 flex items-center justify-between hover:bg-gray-50 cursor-pointer active:bg-gray-100"
                onClick={() => navigate(`/clientes/${c._id}`)}>
                <div>
                  <div className="font-semibold text-gray-800">{c.nome}</div>
                  <div className="text-sm text-gray-500">{c.telefone}{c.cpf ? ` • ${c.cpf}` : ''}</div>
                  {c.email && <div className="text-xs text-gray-400">{c.email}</div>}
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={e => abrirEditar(c, e)} className="text-blue-600 text-sm px-3 py-1.5 rounded-lg hover:bg-blue-50">Editar</button>
                  <button type="button" onClick={e => deletar(c._id, e)} className="text-red-500 text-sm px-3 py-1.5 rounded-lg hover:bg-red-50">Excluir</button>
                </div>
              </div>
            ))}
            {tipoBusca === 'veiculo' && todos.map(v => (
              <div key={v._id} className="p-4 hover:bg-gray-50 cursor-pointer active:bg-gray-100"
                onClick={() => navigate(`/clientes/${v.clienteId}`)}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-800">{v.placa}</span>
                      <span className="text-gray-600">{v.marca} {v.modelo} {v.ano}</span>
                    </div>
                    {v.cliente && (
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full font-medium">👤 {v.cliente.nome}</span>
                        <span className="text-xs text-gray-400">{v.cliente.telefone}</span>
                      </div>
                    )}
                  </div>
                  <span className="text-blue-400 text-sm">Ver cliente →</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Resultados de busca */}
      {buscado && !loading && (
        <>
          <p className="text-sm text-gray-500 mb-2 px-1">
            {resultados.length === 0 ? 'Nenhum resultado encontrado' : `${resultados.length} resultado(s)`}
          </p>

          <div className="bg-white rounded-xl shadow-sm divide-y divide-gray-50">
            {resultados.length === 0 && (
              <div className="text-center py-10">
                <p className="text-gray-400">Nenhum {tipoBusca === 'cliente' ? 'cliente' : 'veículo'} encontrado</p>
                <button onClick={abrirNovo} className="mt-3 text-blue-600 text-sm font-medium hover:underline">
                  + Cadastrar novo cliente
                </button>
              </div>
            )}

            {resultados.map((r, i) => {
              if (r.tipo === 'cliente') {
                const c = r.cliente;
                return (
                  <div key={c._id} className="p-4 flex items-center justify-between hover:bg-gray-50 cursor-pointer active:bg-gray-100"
                    onClick={() => navigate(`/clientes/${c._id}`)}>
                    <div>
                      <div className="font-semibold text-gray-800">{c.nome}</div>
                      <div className="text-sm text-gray-500">{c.telefone}{c.cpf ? ` • ${c.cpf}` : ''}</div>
                      {c.email && <div className="text-xs text-gray-400">{c.email}</div>}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={e => abrirEditar(c, e)} className="text-blue-600 text-sm px-3 py-1.5 rounded-lg hover:bg-blue-50">Editar</button>
                      <button onClick={e => deletar(c._id, e)} className="text-red-500 text-sm px-3 py-1.5 rounded-lg hover:bg-red-50">Excluir</button>
                    </div>
                  </div>
                );
              }

              // Resultado de veículo
              const v = r.veiculo;
              const c = r.cliente;
              return (
                <div key={v._id} className="p-4 hover:bg-gray-50 cursor-pointer active:bg-gray-100"
                  onClick={() => navigate(`/clientes/${c?._id || v.clienteId}`)}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-800">{v.placa}</span>
                        <span className="text-gray-600">{v.marca} {v.modelo} {v.ano}</span>
                        {v.cor && <span className="text-xs text-gray-400">• {v.cor}</span>}
                      </div>
                      <div className="text-sm text-gray-500 mt-0.5">{v.combustivel} • {Number(v.km).toLocaleString()} km</div>
                      {c && (
                        <div className="flex items-center gap-1.5 mt-1.5">
                          <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full font-medium">👤 {c.nome}</span>
                          <span className="text-xs text-gray-400">{c.telefone}</span>
                        </div>
                      )}
                    </div>
                    <span className="text-blue-400 text-sm">Ver cliente →</span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Modal cadastro/edição */}
      {modal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl max-h-[93vh] flex flex-col">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
              <h2 className="text-lg font-semibold">{editando ? 'Editar Cliente' : 'Novo Cliente'}</h2>
              <button type="button" onClick={() => setModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
            </div>
            <form onSubmit={salvar} className="overflow-y-auto flex-1 flex flex-col">
              <div className="p-5 space-y-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Dados do Cliente</p>
                {[
                  { key: 'nome', label: 'Nome *', required: true, placeholder: 'Nome completo' },
                  { key: 'telefone', label: 'Telefone *', required: true, placeholder: '(00) 00000-0000' },
                  { key: 'cpf', label: 'CPF', placeholder: '000.000.000-00' },
                  { key: 'email', label: 'Email', type: 'email', placeholder: 'email@exemplo.com' },
                ].map(f => (
                  <div key={f.key}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{f.label}</label>
                    <input type={f.type || 'text'} required={f.required} placeholder={f.placeholder}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} />
                  </div>
                ))}
                <EnderecoFields form={form} setForm={setForm} />
              </div>
              {!editando && (
                <div className="px-5 pb-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Veículos</p>
                    <button type="button" onClick={adicionarVeiculo} className="text-blue-600 text-sm font-medium hover:underline">+ Adicionar veículo</button>
                  </div>
                  {veiculos.map((v, i) => (
                    <div key={i} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-semibold text-gray-600">Veículo {i + 1}</span>
                        {veiculos.length > 1 && <button type="button" onClick={() => removerVeiculo(i)} className="text-red-400 hover:text-red-600 text-sm">Remover</button>}
                      </div>
                      <VeiculoFormFields value={v} onChange={novo => atualizarVeiculo(i, novo)} compact />
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-3 p-5 border-t border-gray-100 flex-shrink-0 mt-auto">
                <button type="button" onClick={() => setModal(false)} className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg font-medium hover:bg-gray-50">Cancelar</button>
                <button type="submit" disabled={salvando} className="flex-1 bg-blue-600 text-white py-2.5 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
                  {salvando ? 'Salvando...' : editando ? 'Salvar' : 'Cadastrar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
